// Customer-facing label for the rewards "points" currency.
//
// Companies can rename "Points" to their own brand term — e.g. "Rain Cash",
// "Yoli Points" — via company settings (CURRENT-1540 / CURRENT-1568). The
// backend supplies both a singular and a plural form on the company object
// (`reward_points_label_singular` / `reward_points_label_plural`); we use them
// as-is and select by count, falling back to "point" / "points" when a company
// hasn't configured a custom label. The platform keeps using "points"
// internally — only this display label changes.

const DEFAULT_SINGULAR = "point";
const DEFAULT_PLURAL = "points";

type PointsLabelConfig = {
  reward_points_label_singular?: string | null;
  reward_points_label_plural?: string | null;
};

/** Returns the singular or plural label based on `count`. */
export function pointsLabel(count: number, config?: PointsLabelConfig): string {
  const singular =
    config?.reward_points_label_singular?.trim() || DEFAULT_SINGULAR;
  const plural = config?.reward_points_label_plural?.trim() || DEFAULT_PLURAL;
  return Math.abs(count) === 1 ? singular : plural;
}

/** Returns the plural label, for count-agnostic contexts (headings, actions). */
export function pluralPointsLabel(config?: PointsLabelConfig): string {
  return config?.reward_points_label_plural?.trim() || DEFAULT_PLURAL;
}

/**
 * Capitalizes the first character. Used in positions that render a
 * sentence-leading label today (e.g. "Points available") so the default output
 * stays byte-identical. Casing policy for custom brand labels is an open
 * question on the epic (CURRENT-1540) — revisit when decided.
 */
export function capitalizeFirst(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
